/**
 * ImpactBanner — iter-95
 *
 * Sender-impact digest. Surfaces only when a signed-in user has at least
 * one card with ≥1 reaction or open in the last 7 days. The number is
 * the headline — "your cards got felt 47 times this week" — and tapping
 * opens History with the most-reacted card highlighted.
 *
 * Quiet for anon and zero-impact users (no banner = no friction).
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Flame } from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";

export default function ImpactBanner() {
    const [impact, setImpact] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/me/impact`, { withCredentials: true });
                if (!cancelled) setImpact(data);
            } catch {
                if (!cancelled) setImpact(null);
            }
        })();
    }, []);

    if (!impact || !impact.is_signed_in) return null;
    const total = (impact.total_felt || 0) + (impact.total_held || 0);
    if (total < 1) return null;

    const feltCopy =
        impact.total_felt === 1 ? "1 person felt this" : `${impact.total_felt} people felt your cards`;

    return (
        <motion.button
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            onClick={() => navigate("/history")}
            data-testid="impact-banner"
            className="w-full text-left flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-app/[0.04] border border-rose-300/15 hover:border-rose-300/35 transition-colors"
        >
            <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                    <Heart className="w-3.5 h-3.5 text-rose-300" fill="rgb(244,114,182)" strokeWidth={1.6} />
                    {impact.total_held > 0 && (
                        <Flame className="w-3.5 h-3.5 text-amber-300" strokeWidth={1.6} />
                    )}
                </div>
                <div>
                    <p className="font-sans text-[10px] uppercase tracking-[0.28em] text-app-3 mb-0.5">
                        this week
                    </p>
                    <p className="font-serif italic text-app text-[14px] leading-tight">
                        {feltCopy}
                    </p>
                    {impact.total_remixes > 0 && (
                        <p className="font-serif italic text-amber-200/80 text-[11px] mt-0.5">
                            {impact.total_remixes} {impact.total_remixes === 1 ? "remix" : "remixes"} spun out
                        </p>
                    )}
                </div>
            </div>
            <span className="font-sans text-[10px] uppercase tracking-[0.28em] text-app-3">
                see →
            </span>
        </motion.button>
    );
}
